/**
 * ExploreEntrySection — "Discover / Your Private Guide"
 *
 * Reads from GET /api/public/guest-site-config.
 * Hotels and Flights tiles appear only when enabled in Settings → Guest-Facing Content.
 * All expansion panels use the dark glass design system established in the RSVP module.
 * Mobile-first. Graceful fallback if fields are empty or disabled.
 */

import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plane, Compass, Calendar, ChevronLeft, ChevronRight,
         BedDouble, MapPin, ExternalLink, X, Crown, ConciergeBell, Send } from 'lucide-react';
import { isPhase2Released } from '../utils/featureGates';
import { chatWithOscar } from '../data/api';

const SHOW_EXPLORE = isPhase2Released();
const API = process.env.REACT_APP_BACKEND_URL;

// ─── Shared dark glass style (matches RSVP card) ─────────────────────────────
const darkGlass = {
  background: 'rgba(20,15,8,0.58)',
  backdropFilter: 'blur(18px) saturate(1.20)',
  WebkitBackdropFilter: 'blur(18px) saturate(1.20)',
  border: '1.5px solid rgba(212,168,83,0.55)',
  boxShadow: [
    'inset 0 2px 0 rgba(255,255,255,0.14)',
    'inset -1px 0 0 rgba(255,255,255,0.08)',
    'inset 1px 0 0 rgba(255,255,255,0.08)',
    'inset 0 -1px 0 rgba(212,168,83,0.30)',
    '0 24px 48px rgba(0,0,0,0.28)',
    '0 6px 16px rgba(0,0,0,0.16)',
  ].join(', '),
};

// Panel background — lighter than the dark card (expanded detail panels)
const panelGlass = {
  background: 'rgba(255,252,240,0.92)',
  border: '1px solid rgba(200,170,60,0.45)',
  borderRadius: 20,
  boxShadow: '0 24px 50px rgba(0,0,0,0.14), inset 0 1.5px 0 rgba(255,255,255,0.90)',
};

// ─── Gold divider ─────────────────────────────────────────────────────────────
const GoldDivider = () => (
  <div style={{ display:'flex', alignItems:'center', gap:8, margin:'14px 0' }}>
    <div style={{ flex:1, height:1, background:'linear-gradient(90deg, transparent, rgba(180,140,30,0.50))' }} />
    <span style={{ color:'rgba(180,140,30,0.70)', fontSize:9 }}>✦</span>
    <div style={{ flex:1, height:1, background:'linear-gradient(90deg, rgba(180,140,30,0.50), transparent)' }} />
  </div>
);

// ─── Hotels expansion panel ───────────────────────────────────────────────────
function HotelsPanel({ config, onClose }) {
  const h = config?.travel?.hotels || {};
  const ph = h.primary_hotel || {};
  const extras = h.additional_hotels || [];

  const mapsUrl = (address) =>
    `https://maps.google.com/?q=${encodeURIComponent(address)}`;

  // Fallback: nothing configured
  const hasContent = h.room_block_enabled && (ph.name || ph.booking_url);

  return (
    <div className="animate-in slide-in-from-bottom-8 fade-in duration-700" style={{ marginTop:0 }}>
      <div style={{ ...panelGlass, padding:'24px 20px', position:'relative', overflow:'hidden' }}>
        {/* Decorative top sheen */}
        <div style={{ position:'absolute', top:0, left:0, right:0, height:1.5, pointerEvents:'none',
          background:'linear-gradient(90deg, transparent, rgba(255,255,255,0.88) 30%, rgba(255,255,255,0.88) 70%, transparent)' }} />

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <BedDouble size={18} color="rgba(160,120,20,0.80)" />
            <p style={{ fontFamily:"'Cinzel',serif", fontSize:14, fontWeight:700,
              color:'#1E1A14', margin:0, letterSpacing:'1px', textTransform:'uppercase' }}>
              Hotels
            </p>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer',
            padding:4, color:'rgba(160,120,20,0.70)', borderRadius:8 }}>
            <X size={18} />
          </button>
        </div>

        {!hasContent ? (
          // ── Fallback: nothing configured ──
          <p style={{ fontSize:13, color:'rgba(120,90,20,0.70)', fontStyle:'italic',
            textAlign:'center', padding:'20px 0', margin:0 }}>
            Hotel details coming soon — check back closer to the event.
          </p>
        ) : (
          <>
            {/* Host Recommended */}
            {h.room_block_enabled && (
              <>
                <p style={{ fontSize:9, fontWeight:700, textTransform:'uppercase',
                  letterSpacing:'2px', color:'rgba(140,100,20,0.70)', margin:'14px 0 10px' }}>
                  Host Recommended
                </p>

                {/* Hotel name + address */}
                {ph.name && (
                  <p style={{ fontFamily:"'Cinzel',serif", fontSize:16, fontWeight:600,
                    color:'#1E1A14', margin:'0 0 4px', letterSpacing:'0.3px' }}>
                    {ph.name}
                  </p>
                )}
                {ph.address && (
                  <button onClick={() => window.open(mapsUrl(ph.address), '_blank')}
                    style={{ display:'flex', alignItems:'center', gap:5, background:'none',
                      border:'none', cursor:'pointer', padding:0, marginBottom:14 }}>
                    <MapPin size={12} color="rgba(160,120,20,0.80)" />
                    <span style={{ fontSize:12, color:'rgba(140,100,20,0.85)',
                      textDecoration:'underline', textUnderlineOffset:2 }}>
                      {ph.address}
                    </span>
                  </button>
                )}

                {/* Discount code */}
                {ph.discount_code && (
                  <div style={{ display:'inline-flex', alignItems:'center', gap:8,
                    padding:'7px 14px', borderRadius:9, marginBottom:14,
                    background:'rgba(198,167,90,0.12)', border:'1px solid rgba(198,167,90,0.38)' }}>
                    <span style={{ fontSize:10, fontWeight:600, textTransform:'uppercase',
                      letterSpacing:'1px', color:'rgba(140,100,20,0.80)' }}>Discount Code</span>
                    <span style={{ fontFamily:"'Cinzel',serif", fontSize:13, fontWeight:700,
                      color:'#9B7420', letterSpacing:'1.5px' }}>{ph.discount_code}</span>
                  </div>
                )}

                {/* Booking CTA */}
                {ph.booking_url && (
                  <button
                    onClick={() => window.open(ph.booking_url, '_blank')}
                    data-testid="book-room-block-btn"
                    style={{
                      width:'100%', height:52, borderRadius:13, cursor:'pointer',
                      background:'linear-gradient(180deg, #1A1A1A 0%, #0D0D0D 50%, #000000 100%)',
                      border:'1px solid rgba(200,170,60,0.65)',
                      boxShadow:'0 4px 16px rgba(0,0,0,0.18), inset 0 1px 0 rgba(212,168,83,0.15)',
                      fontFamily:"'Cinzel',serif", fontSize:12, fontWeight:700,
                      color:'#D4A853', letterSpacing:'2px', textTransform:'uppercase',
                      display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                    }}>
                    <ExternalLink size={14} />
                    Book Room Block
                  </button>
                )}
              </>
            )}

            {/* Additional hotels */}
            {extras.length > 0 && (
              <>
                <GoldDivider />
                <p style={{ fontSize:9, fontWeight:700, textTransform:'uppercase',
                  letterSpacing:'2px', color:'rgba(140,100,20,0.70)', margin:'0 0 10px' }}>
                  Nearby Options
                </p>
                {extras.map((hotel, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                    padding:'10px 0', borderBottom: i < extras.length - 1 ? '1px solid rgba(198,167,90,0.18)' : 'none' }}>
                    <div>
                      <p style={{ fontFamily:"'Cinzel',serif", fontSize:13, fontWeight:600,
                        color:'#1E1A14', margin:0, letterSpacing:'0.3px' }}>
                        {hotel.name}
                      </p>
                      {hotel.address && (
                        <p style={{ fontSize:11, color:'rgba(120,90,20,0.75)',
                          margin:'2px 0 0', fontStyle:'italic' }}>
                          {hotel.address}
                        </p>
                      )}
                    </div>
                    {hotel.address && (
                      <button onClick={() => window.open(mapsUrl(hotel.address), '_blank')}
                        style={{ background:'rgba(198,167,90,0.10)', border:'1px solid rgba(198,167,90,0.30)',
                          borderRadius:8, padding:'7px 10px', cursor:'pointer', flexShrink:0, marginLeft:8 }}>
                        <MapPin size={14} color="rgba(160,120,20,0.80)" />
                      </button>
                    )}
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Flights expansion panel ──────────────────────────────────────────────────
function FlightsPanel({ config, onClose }) {
  const fl = config?.travel?.flights || {};
  const airport = fl.preferred_airport || 'EWR';
  const tips = fl.tips || '';
  const allAirports = ['EWR', 'JFK', 'LGA'];

  const airportNames = { EWR: 'Newark Liberty', JFK: 'John F. Kennedy', LGA: 'LaGuardia' };

  const searchFlights = (apt) => {
    const url = `https://www.google.com/travel/flights?q=flights+to+${apt}`;
    window.open(url, '_blank');
  };

  const hasContent = fl.preferred_airport || fl.tips;

  return (
    <div className="animate-in slide-in-from-bottom-8 fade-in duration-700" style={{ marginTop:0 }}>
      <div style={{ ...panelGlass, padding:'24px 20px', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:0, left:0, right:0, height:1.5, pointerEvents:'none',
          background:'linear-gradient(90deg, transparent, rgba(255,255,255,0.88) 30%, rgba(255,255,255,0.88) 70%, transparent)' }} />

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <Plane size={18} color="rgba(160,120,20,0.80)" />
            <p style={{ fontFamily:"'Cinzel',serif", fontSize:14, fontWeight:700,
              color:'#1E1A14', margin:0, letterSpacing:'1px', textTransform:'uppercase' }}>
              Flights
            </p>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer',
            padding:4, color:'rgba(160,120,20,0.70)', borderRadius:8 }}>
            <X size={18} />
          </button>
        </div>

        {!hasContent ? (
          <p style={{ fontSize:13, color:'rgba(120,90,20,0.70)', fontStyle:'italic',
            textAlign:'center', padding:'20px 0', margin:0 }}>
            Flight information coming soon — check back closer to the event.
          </p>
        ) : (
          <>
            {/* Airport selector — horizontal snap carousel */}
            <p style={{ fontSize:13, fontWeight:700, textTransform:'uppercase',
              letterSpacing:'2px', color:'rgba(140,100,20,0.70)', margin:'14px 0 12px' }}>
              Nearby Airports
            </p>
            <div style={{ overflowX:'auto', scrollSnapType:'x mandatory',
              scrollbarWidth:'none', msOverflowStyle:'none', marginBottom:16,
              display:'flex', gap:10, paddingBottom:4 }}>
              {allAirports.map(apt => {
                const isPreferred = apt === airport;
                const distanceNote = { EWR:'~8 miles from venue', JFK:'~25 miles', LGA:'~22 miles' };
                return (
                  <div key={apt} style={{
                    flexShrink:0, scrollSnapAlign:'start',
                    width:160, borderRadius:16, padding:'18px 14px',
                    textAlign:'center', position:'relative', overflow:'hidden',
                    background: isPreferred
                      ? 'linear-gradient(145deg, rgba(198,167,90,0.22), rgba(198,167,90,0.10))'
                      : 'rgba(255,253,246,0.85)',
                    border: isPreferred
                      ? '2px solid rgba(198,167,90,0.70)'
                      : '1px solid rgba(198,167,90,0.28)',
                    boxShadow: isPreferred
                      ? '0 0 20px rgba(198,167,90,0.25), inset 0 1.5px 0 rgba(255,255,255,0.80)'
                      : 'inset 0 1.5px 0 rgba(255,255,255,0.70), 0 2px 8px rgba(140,100,20,0.08)',
                    transition:'all 0.25s ease',
                    cursor:'pointer',
                  }}>
                    {/* Gold top bar on preferred */}
                    {isPreferred && (
                      <div style={{ position:'absolute', top:0, left:0, right:0, height:3,
                        background:'linear-gradient(90deg, transparent, rgba(198,167,90,0.90) 30%, rgba(220,185,60,1) 50%, rgba(198,167,90,0.90) 70%, transparent)',
                        pointerEvents:'none' }} />
                    )}
                    {/* Airport code */}
                    <p style={{ fontFamily:"'Cinzel',serif", fontSize:28, fontWeight:700,
                      color: isPreferred ? '#9B7420' : '#5A4E38',
                      margin:0, letterSpacing:'2px', lineHeight:1 }}>
                      {apt}
                    </p>
                    {/* Full name */}
                    <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:15, fontStyle:'italic',
                      color:'rgba(100,75,20,0.80)', margin:'6px 0 4px', lineHeight:1.3 }}>
                      {airportNames[apt]}
                    </p>
                    {/* Distance */}
                    <p style={{ fontSize:13, color:'rgba(120,90,20,0.65)', margin:'4px 0 0' }}>
                      {distanceNote[apt]}
                    </p>
                    {/* Closest badge */}
                    {isPreferred && (
                      <div style={{ display:'inline-flex', alignItems:'center', gap:4,
                        marginTop:8, padding:'3px 10px', borderRadius:99,
                        background:'rgba(198,167,90,0.14)', border:'1px solid rgba(198,167,90,0.40)' }}>
                        <span style={{ fontSize:11, fontWeight:700, textTransform:'uppercase',
                          letterSpacing:'0.8px', color:'rgba(140,100,20,0.90)' }}>★ Closest</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Search CTA */}
            <button
              onClick={() => searchFlights(airport)}
              data-testid="search-google-flights-btn"
              style={{
                width:'100%', height:50, borderRadius:13, cursor:'pointer',
                background:'linear-gradient(180deg, #1A1A1A 0%, #0D0D0D 50%, #000000 100%)',
                border:'1px solid rgba(200,170,60,0.65)',
                boxShadow:'0 4px 16px rgba(0,0,0,0.18), inset 0 1px 0 rgba(212,168,83,0.15)',
                fontFamily:"'Cinzel',serif", fontSize:11, fontWeight:700,
                color:'#D4A853', letterSpacing:'1.5px', textTransform:'uppercase',
                display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                marginBottom:14,
              }}>
              <ExternalLink size={14} />
              Search on Google Flights
            </button>

            {/* Travel tips */}
            {tips && (
              <>
                <GoldDivider />
                <p style={{ fontSize:13, fontWeight:700, textTransform:'uppercase',
                  letterSpacing:'2px', color:'rgba(140,100,20,0.70)', margin:'0 0 8px' }}>
                  Travel Tips
                </p>
                <p style={{ fontSize:15, color:'#3A3020', lineHeight:1.70, margin:0,
                  fontFamily:"'Cormorant Garamond',serif", fontStyle:'italic', fontWeight:400 }}>
                  {tips}
                </p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── NJ Guide expansion panel ─────────────────────────────────────────────────
// Categories list & map URL & all visible strings are admin-controllable via
// GuestExperienceEditor → NJ Guide tab. Defaults below preserve current live
// output byte-for-byte until admin opts to change something.

const MAP_URL_DEFAULT = "https://maps.google.com/maps?q=644+Pascack+Rd+Township+of+Washington+NJ+07676&z=15&output=embed";

const CATEGORIES_DEFAULT = [
  { key: 'all',           label: 'All' },
  { key: 'venue_hotels',  label: 'Venue & Hotels' },
  { key: 'moms_favorites',label: "Mom's Favorites" },
  { key: 'food_dining',   label: 'Food & Dining' },
  { key: 'activities',    label: 'Activities' },
  { key: 'must_visits',   label: 'Must-Visits' },
];

function NJGuidePanel({ config, onClose }) {
  const [activeCategory, setActiveCategory] = useState('all');
  const [activePlaceId,  setActivePlaceId]  = useState(null);

  // Admin-controllable strings — fall back to current literals when unset, so
  // live behavior is byte-identical until admin opts to edit. (Feb 2026.)
  const ttd        = config?.things_to_do || {};
  const tileTitle  = (ttd.tile_title && ttd.tile_title.trim()) || 'NJ Guide';
  const mapUrl     = (ttd.map_url && ttd.map_url.trim()) || MAP_URL_DEFAULT;
  const mapVisible = ttd.map_visible !== false; // default true
  const categories = (Array.isArray(ttd.categories) && ttd.categories.length > 0)
    ? ttd.categories : CATEGORIES_DEFAULT;
  const emptyTitle = (ttd.empty_state_title && ttd.empty_state_title.trim()) || 'Curated places coming soon';
  const emptyHint  = (ttd.empty_state_hint && ttd.empty_state_hint.trim()) || 'Add places in Settings → Guest-Facing Content';
  const categoryEmpty = (ttd.category_empty_text && ttd.category_empty_text.trim()) || 'No places in this category yet.';

  const allPlaces = ttd.places || [];
  const filtered  = activeCategory === 'all'
    ? allPlaces
    : allPlaces.filter(p => p.category === activeCategory);

  const mapsUrl = (address) =>
    `https://maps.google.com/?q=${encodeURIComponent(address)}`;

  const hasPlaces = allPlaces.length > 0;

  return (
    <div className="animate-in slide-in-from-bottom-8 fade-in duration-700" style={{ marginTop:0 }}>
      <div style={{ ...panelGlass, padding:'0 0 20px', position:'relative', overflow:'hidden' }}>
        {/* Top bevel */}
        <div style={{ position:'absolute', top:0, left:0, right:0, height:1.5, pointerEvents:'none',
          background:'linear-gradient(90deg, transparent, rgba(255,255,255,0.88) 30%, rgba(255,255,255,0.88) 70%, transparent)' }} />

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'20px 20px 14px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <MapPin size={18} color="rgba(160,120,20,0.80)" />
            <p style={{ fontFamily:"'Cinzel',serif", fontSize:14, fontWeight:700,
              color:'#1E1A14', margin:0, letterSpacing:'1px', textTransform:'uppercase' }}>
              {tileTitle}
            </p>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer',
            padding:4, color:'rgba(160,120,20,0.70)', borderRadius:8 }}>
            <X size={18} />
          </button>
        </div>

        {/* Map embed — admin-configurable URL + visibility */}
        {mapVisible && (
          <div style={{ margin:'0 0 16px', position:'relative', height:180, overflow:'hidden' }}>
            <iframe
              src={mapUrl}
              title="Venue area map"
              width="100%" height="180"
              style={{ border:'none', display:'block' }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            {/* Lavender edge glow overlay */}
            <div style={{ position:'absolute', inset:0, pointerEvents:'none',
              boxShadow:'inset 0 0 0 2px rgba(175,148,230,0.35), inset 0 0 20px rgba(175,148,230,0.08)' }} />
          </div>
        )}

        {/* Category filters */}
        <div style={{ padding:'0 16px 14px', overflowX:'auto', scrollbarWidth:'none' }}>
          <div style={{ display:'flex', gap:7, minWidth:'max-content' }}>
            {categories.map(cat => (
              <button key={cat.key} onClick={() => setActiveCategory(cat.key)}
                style={{
                  padding:'7px 14px', borderRadius:99, border:'none', cursor:'pointer',
                  fontFamily:"'Cinzel',serif", fontSize:11, fontWeight:700,
                  textTransform:'uppercase', letterSpacing:'0.8px',
                  background: activeCategory === cat.key
                    ? 'linear-gradient(180deg, #1A1A1A 0%, #0D0D0D 50%, #000000 100%)'
                    : 'rgba(255,253,246,0.80)',
                  color: activeCategory === cat.key ? '#D4A853' : '#7A6A40',
                  boxShadow: activeCategory === cat.key
                    ? '0 4px 14px rgba(0,0,0,0.22), inset 0 1px 0 rgba(212,168,83,0.12)'
                    : '0 1px 3px rgba(140,100,20,0.08), inset 0 1px 0 rgba(255,255,255,0.70)',
                  border: activeCategory === cat.key
                    ? '1px solid rgba(200,170,60,0.65)'
                    : '1px solid rgba(198,167,90,0.28)',
                  transition:'all 0.2s ease',
                  whiteSpace:'nowrap',
                }}>
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        <GoldDivider />

        {/* Discovery feed */}
        <div style={{ padding:'0 16px' }}>
          {!hasPlaces ? (
            /* Empty state */
            <div style={{ textAlign:'center', padding:'20px 0' }}>
              <MapPin size={28} color="rgba(160,120,20,0.35)" style={{ marginBottom:10 }} />
              <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:14, fontStyle:'italic',
                color:'rgba(120,90,20,0.70)', margin:0 }}>
                {emptyTitle}
              </p>
              <p style={{ fontSize:10, color:'rgba(120,90,20,0.55)', margin:'6px 0 0' }}>
                {emptyHint}
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <p style={{ fontSize:12, color:'rgba(120,90,20,0.65)', fontStyle:'italic',
              textAlign:'center', padding:'12px 0' }}>
              {categoryEmpty}
            </p>
          ) : (
            <div style={{ overflowX:'auto', scrollbarWidth:'none' }}>
              <div style={{ display:'flex', gap:12, minWidth:'max-content', paddingBottom:4 }}>
                {filtered.map((place, i) => (
                  <div key={i}
                    onClick={() => setActivePlaceId(activePlaceId === i ? null : i)}
                    style={{
                      width:160, flexShrink:0, borderRadius:16, overflow:'hidden',
                      background:'rgba(255,253,246,0.92)',
                      border: place.is_host_favorite
                        ? '1.5px solid rgba(175,148,230,0.55)'
                        : '1px solid rgba(198,167,90,0.28)',
                      boxShadow: place.is_host_favorite
                        ? '0 0 14px rgba(175,148,230,0.22), 0 4px 12px rgba(0,0,0,0.08)'
                        : '0 4px 12px rgba(0,0,0,0.07)',
                      cursor:'pointer', transition:'transform 0.25s ease',
                    }}>
                    {/* Photo or placeholder */}
                    <div style={{ height:90, position:'relative', overflow:'hidden',
                      background: place.photo_url
                        ? 'transparent'
                        : 'linear-gradient(145deg, rgba(198,167,90,0.15), rgba(175,148,230,0.10))' }}>
                      {place.photo_url ? (
                        <img src={place.photo_url} alt={place.name}
                          style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                      ) : (
                        <div style={{ width:'100%', height:'100%', display:'flex',
                          alignItems:'center', justifyContent:'center' }}>
                          <MapPin size={28} color="rgba(160,120,20,0.45)" />
                        </div>
                      )}
                      {/* Carol's Choice crown */}
                      {place.is_host_favorite && (
                        <div style={{ position:'absolute', top:6, right:6,
                          background:'rgba(175,148,230,0.85)', borderRadius:999,
                          width:24, height:24, display:'flex', alignItems:'center', justifyContent:'center',
                          boxShadow:'0 0 8px rgba(175,148,230,0.55)' }}>
                          <Crown size={12} color="#fff" />
                        </div>
                      )}
                    </div>
                    {/* Name */}
                    <div style={{ padding:'10px 10px 8px' }}>
                      <p style={{ fontFamily:"'Cinzel',serif", fontSize:11, fontWeight:600,
                        color:'#1E1A14', margin:'0 0 4px', letterSpacing:'0.3px',
                        lineHeight:1.3, display:'-webkit-box',
                        WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
                        {place.name || 'Place Name'}
                      </p>
                      {/* Host note — expands on tap */}
                      {activePlaceId === i && place.host_note && (
                        <div style={{ margin:'6px 0', padding:'7px 10px', borderRadius:8,
                          background:'rgba(175,148,230,0.12)',
                          border:'1px solid rgba(175,148,230,0.35)' }}>
                          <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:11,
                            fontStyle:'italic', color:'#3A3060', margin:0, lineHeight:1.5 }}>
                            "{place.host_note}"
                          </p>
                        </div>
                      )}
                      {/* Get directions */}
                      {place.address && (
                        <button
                          onClick={e => { e.stopPropagation(); window.open(mapsUrl(place.address), '_blank'); }}
                          style={{ marginTop:6, width:'100%', padding:'6px 0', borderRadius:8,
                            background:'rgba(198,167,90,0.12)', border:'1px solid rgba(198,167,90,0.35)',
                            cursor:'pointer', fontSize:9, fontWeight:700, textTransform:'uppercase',
                            letterSpacing:'0.8px', color:'#9B7420',
                            display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
                          <MapPin size={10} /> Get Directions
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Guest Calendar Panel ─────────────────────────────────────────────────────
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_INITIALS = ['S','M','T','W','T','F','S'];

function GuestCalendarPanel({ onClose }) {
  const [events, setEvents]       = useState([]);
  const [viewDate, setViewDate]   = useState(new Date(2026, 6, 1)); // July 2026
  const [selectedDate, setSelectedDate] = useState(null);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/public/calendar-events`)
      .then(r => r.json())
      .then(d => setEvents(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const todayStr = new Date().toISOString().slice(0,10);

  // Build month grid
  const year  = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const cells = Array(firstDay).fill(null).concat(
    Array.from({length: daysInMonth}, (_, i) => i + 1)
  );
  while (cells.length % 7 !== 0) cells.push(null);

  const eventsOnDate = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return events.filter(e => e.date === dateStr);
  };

  const selectedEvents = selectedDate ? eventsOnDate(selectedDate) : [];
  const selectedDateStr = selectedDate
    ? `${year}-${String(month+1).padStart(2,'0')}-${String(selectedDate).padStart(2,'0')}`
    : null;

  const generateICS = (ev) => {
    const dateParts = ev.date.replace(/-/g,'');
    const start = ev.start_time ? ev.start_time.replace(':','') + '00' : '120000';
    const end   = ev.end_time   ? ev.end_time.replace(':','')   + '00' : '140000';
    const ics = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//CV80//EN',
      'BEGIN:VEVENT',
      `DTSTART:${dateParts}T${start}`,
      `DTEND:${dateParts}T${end}`,
      `SUMMARY:${ev.title}`,
      ev.location ? `LOCATION:${ev.location}` : '',
      ev.note ? `DESCRIPTION:${ev.note}` : '',
      'END:VEVENT', 'END:VCALENDAR'
    ].filter(Boolean).join('\r\n');
    const blob = new Blob([ics], {type:'text/calendar'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download=`${ev.title}.ics`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="animate-in slide-in-from-bottom-8 fade-in duration-700" style={{marginTop:0}}>
      <div style={{...panelGlass, padding:'0 0 20px', position:'relative', overflow:'hidden'}}>
        <div style={{position:'absolute',top:0,left:0,right:0,height:1.5,pointerEvents:'none',
          background:'linear-gradient(90deg,transparent,rgba(255,255,255,0.88) 30%,rgba(255,255,255,0.88) 70%,transparent)' }} />

        {/* Header */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'20px 20px 14px'}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <Calendar size={18} color="rgba(160,120,20,0.80)" />
            <p style={{fontFamily:"'Cinzel',serif",fontSize:14,fontWeight:700,color:'#1E1A14',margin:0,letterSpacing:'1px',textTransform:'uppercase'}}>
              Carol's Schedule
            </p>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',padding:4,color:'rgba(160,120,20,0.70)',borderRadius:8}}>
            <X size={18} />
          </button>
        </div>

        {/* Live ticker */}
        <p style={{fontSize:13,color:'rgba(140,100,20,0.70)',textAlign:'center',margin:'0 0 14px',letterSpacing:'0.5px',fontStyle:'italic'}}>
          Schedule current as of {new Date().toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}
        </p>

        {/* Month navigation */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'0 20px 12px'}}>
          <button onClick={() => setViewDate(new Date(year, month-1, 1))}
            style={{background:'rgba(198,167,90,0.10)',border:'1px solid rgba(198,167,90,0.30)',borderRadius:8,padding:'6px 12px',cursor:'pointer',fontSize:12,color:'#9B7420'}}>‹</button>
          <p style={{fontFamily:"'Cinzel',serif",fontSize:15,fontWeight:700,color:'#1E1A14',margin:0,letterSpacing:'0.5px'}}>
            {MONTH_NAMES[month]} {year}
          </p>
          <button onClick={() => setViewDate(new Date(year, month+1, 1))}
            style={{background:'rgba(198,167,90,0.10)',border:'1px solid rgba(198,167,90,0.30)',borderRadius:8,padding:'6px 12px',cursor:'pointer',fontSize:12,color:'#9B7420'}}>›</button>
        </div>

        {/* Day header */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',padding:'0 16px',marginBottom:4}}>
          {DAY_INITIALS.map((d,i) => (
            <p key={i} style={{textAlign:'center',fontSize:13,fontWeight:700,textTransform:'uppercase',
              letterSpacing:'0.8px',color:'rgba(140,100,20,0.65)',margin:0,padding:'4px 0'}}>
              {d}
            </p>
          ))}
        </div>

        {/* Calendar grid — large and legible */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:1,padding:'0 16px',marginBottom:14}}>
          {cells.map((day, i) => {
            const dayEvents = day ? eventsOnDate(day) : [];
            const isSelected = day === selectedDate;
            const isUnavailable = dayEvents.some(e => e.event_type === 'unavailable');
            const hasSchedule  = dayEvents.some(e => e.event_type === 'schedule');
            const dateStr = day ? `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}` : null;
            const isToday = dateStr === todayStr;
            return (
              <div key={i} onClick={() => day && setSelectedDate(day === selectedDate ? null : day)}
                style={{
                  minHeight: 44, borderRadius: 10, display:'flex', flexDirection:'column',
                  alignItems:'center', justifyContent:'center', cursor: day ? 'pointer' : 'default',
                  background: isSelected ? 'rgba(198,167,90,0.18)'
                    : isUnavailable ? 'rgba(175,148,230,0.12)'
                    : 'transparent',
                  border: isSelected ? '1.5px solid rgba(198,167,90,0.60)'
                    : isToday ? '1.5px solid rgba(198,167,90,0.35)'
                    : '1px solid transparent',
                  transition:'all 0.15s ease',
                }}>
                {day && (
                  <>
                    <span style={{fontSize:16, fontWeight: isToday||isSelected ? 700 : 500,
                      color: isSelected ? '#9B7420' : isUnavailable ? '#7060A0' : '#1E1A14',
                      lineHeight:1}}>
                      {day}
                    </span>
                    {/* Event indicators */}
                    <div style={{display:'flex',gap:2,marginTop:3}}>
                      {hasSchedule   && <div style={{width:5,height:5,borderRadius:'50%',background:'rgba(198,167,90,0.80)'}} />}
                      {isUnavailable && <div style={{width:5,height:5,borderRadius:'50%',background:'rgba(175,148,230,0.80)'}} />}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Selected date detail */}
        {selectedDate && (
          <div style={{margin:'0 16px',padding:'14px',borderRadius:14,
            background:'rgba(255,253,246,0.90)',border:'1px solid rgba(198,167,90,0.28)',
            boxShadow:'inset 0 1px 0 rgba(255,255,255,0.80)'}}>
            <p style={{fontFamily:"'Cinzel',serif",fontSize:13,fontWeight:700,color:'#1E1A14',margin:'0 0 10px',letterSpacing:'0.3px'}}>
              {new Date(selectedDateStr + 'T12:00:00').toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'})}
            </p>
            {selectedEvents.length === 0 ? (
              <p style={{fontSize:12,color:'rgba(120,90,20,0.65)',margin:0,fontStyle:'italic'}}>No events on this date.</p>
            ) : selectedEvents.map((ev, i) => (
              <div key={i} style={{marginBottom: i < selectedEvents.length-1 ? 12 : 0}}>
                {ev.event_type === 'unavailable' ? (
                  <div style={{padding:'10px 12px',borderRadius:10,background:'rgba(175,148,230,0.14)',border:'1px solid rgba(175,148,230,0.40)'}}>
                    <p style={{fontSize:12,fontWeight:700,color:'#6050A0',margin:'0 0 2px'}}>🔒 {ev.title || 'Carol is Unavailable'}</p>
                    <p style={{fontSize:11,color:'rgba(90,70,150,0.80)',margin:0,fontStyle:'italic'}}>
                      Carol is at a private engagement. Please check another time.
                    </p>
                  </div>
                ) : (
                  <div>
                    <p style={{fontFamily:"'Cinzel',serif",fontSize:13,fontWeight:600,color:'#1E1A14',margin:'0 0 2px'}}>{ev.title}</p>
                    {ev.start_time && <p style={{fontSize:11,color:'rgba(120,90,20,0.75)',margin:'0 0 2px'}}>⏰ {ev.start_time}{ev.end_time ? ` – ${ev.end_time}` : ''}</p>}
                    {ev.location   && <p style={{fontSize:11,color:'rgba(120,90,20,0.75)',margin:'0 0 4px'}}>📍 {ev.location}</p>}
                    {ev.note       && <p style={{fontSize:12,color:'#3A3020',fontStyle:'italic',margin:'0 0 8px',lineHeight:1.5,fontFamily:"'Cormorant Garamond',serif"}}>"{ev.note}"</p>}
                    <button onClick={() => generateICS(ev)}
                      style={{padding:'8px 16px',borderRadius:10,cursor:'pointer',
                        background:'linear-gradient(180deg, #1A1A1A 0%, #0D0D0D 50%, #000000 100%)',
                        border:'1px solid rgba(200,170,60,0.65)',
                        boxShadow:'0 4px 14px rgba(0,0,0,0.20), inset 0 1px 0 rgba(212,168,83,0.12)',
                        fontSize:10,fontWeight:700,color:'#D4A853',letterSpacing:'1px',
                        textTransform:'uppercase',display:'flex',alignItems:'center',gap:6}}>
                      📅 Add to My Calendar
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Legend */}
        <div style={{display:'flex',gap:14,justifyContent:'center',marginTop:14,padding:'0 16px'}}>
          <div style={{display:'flex',alignItems:'center',gap:5}}>
            <div style={{width:8,height:8,borderRadius:'50%',background:'rgba(198,167,90,0.80)'}} />
            <span style={{fontSize:13,color:'rgba(120,90,20,0.70)',fontWeight:600}}>Event</span>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:5}}>
            <div style={{width:8,height:8,borderRadius:'50%',background:'rgba(175,148,230,0.80)'}} />
            <span style={{fontSize:13,color:'rgba(90,70,130,0.70)',fontWeight:600}}>Carol Unavailable</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Concierge (Oscar) Panel ──────────────────────────────────────────────────
// Apr 2026: Oscar's visual identity is now the robot concierge asset
// (`/oscar-tile.webp`, self-hosted under /app/frontend/public/). Replaces the
// previous dog-themed avatar.
const OSCAR_IMG = '/oscar-tile.webp';

export function ConciergePanel({ guestName, displayName, conciergeConfig, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input,    setInput]    = useState('');
  const [sending,  setSending]  = useState(false);
  const [sessionId] = useState(() => 'conc-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7));
  const endRef = useRef(null);

  const greeting = conciergeConfig?.greeting_template
    ? conciergeConfig.greeting_template.replace(/\{\{first_name\}\}/g, guestName || 'Guest')
    : (guestName ? `Hey There ${guestName},` : 'Hey There!');
  const introText = conciergeConfig?.intro_text || "I'm here to help make Carol's celebration feel effortless. How can I help?";

  const sendMsg = async (text) => {
    const msg = (text || input).trim();
    if (!msg) return;
    setInput('');
    setMessages(p => [...p, { from: 'user', text: msg }]);
    setSending(true);
    try {
      const res = await chatWithOscar(msg, sessionId);
      setMessages(p => [...p, { from: 'oscar', text: res.reply }]);
    } catch {
      setMessages(p => [...p, { from: 'oscar', text: 'My apologies — something went wrong. Please try again in a moment.' }]);
    } finally {
      setSending(false);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  return (
    <div className="animate-in slide-in-from-bottom-8 fade-in duration-700" style={{ marginTop: 0 }}>
      <div style={{ ...panelGlass, padding: '20px', position: 'relative', overflow: 'hidden',
        minHeight: '75vh', display: 'flex', flexDirection: 'column' }}>
        {/* Top sheen */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1.5, pointerEvents: 'none',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.88) 30%, rgba(255,255,255,0.88) 70%, transparent)' }} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ConciergeBell size={18} color="rgba(160,120,20,0.80)" />
            <p style={{ fontFamily: "'Cinzel',serif", fontSize: 15, fontWeight: 700,
              color: '#0D0D0D', margin: 0, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              {/* Personalized title — uses the same display-name rule as the
                  RSVP greeting and Compose Invite (Feb 2026):
                    - Solo "Evelyn"          → "Evelyn's Personal Concierge"
                    - Multi "Carol & Lito"   → "Carol & Lito's Personal Concierge"
                    - "Lavarias Family"      → "Lavarias Family Personal Concierge"
                    - "Gia and Guests"       → "Gia and Guests' Personal Concierge"
                  Falls back to the original brand title when no token context. */}
              {(() => {
                const dn = (displayName || '').trim();
                if (!dn) return 'Personal Concierge';
                // Possessive: trailing "s" → just apostrophe; otherwise 's
                const endsInS = /s\s*$/i.test(dn);
                const endsInFamily = /\bFamily\s*$/i.test(dn);
                if (endsInFamily) return `${dn} Personal Concierge`;
                return endsInS ? `${dn}' Personal Concierge` : `${dn}'s Personal Concierge`;
              })()}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer',
            padding: 4, color: 'rgba(160,120,20,0.70)', borderRadius: 8 }}>
            <X size={18} />
          </button>
        </div>

        {/* TOP: Oscar avatar + speech bubble — horizontal row */}
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 16 }}>
          <img src={OSCAR_IMG} alt="Oscar"
            style={{ width: 76, height: 76, flexShrink: 0,
              borderRadius: 12, objectFit: 'cover', objectPosition: 'center top', display: 'block',
              border: '1.5px solid rgba(200,170,60,0.40)',
              boxShadow: '0 4px 16px rgba(140,100,20,0.22)' }} />
          <div style={{ flex: 1, background: 'rgba(255,252,240,0.93)',
            border: '1px solid rgba(200,170,60,0.50)', borderRadius: 12,
            padding: '12px 14px' }}>
            <p style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 16, fontStyle: 'italic',
              color: '#1A1A1A', margin: '0 0 4px', lineHeight: 1.6 }}>
              "{greeting}
            </p>
            <p style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 16, fontStyle: 'italic',
              color: '#1A1A1A', margin: 0, lineHeight: 1.6 }}>
              {introText}"
            </p>
          </div>
        </div>

        {/* Gold horizontal divider */}
        <div style={{ height: 1, margin: '0 0 16px',
          background: 'linear-gradient(90deg, transparent, rgba(200,170,60,0.65) 20%, rgba(200,170,60,0.65) 80%, transparent)' }} />

        {/* BOTTOM: Chat messages + input — fills remaining panel height */}
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div style={{ overflowY: 'auto', flex: 1, marginBottom: 10, minHeight: 0 }}>
            {messages.length === 0 && (
              <p style={{ fontSize: 14, color: 'rgba(60,50,30,0.65)', fontStyle: 'italic',
                textAlign: 'center', padding: '20px 8px', margin: 0, lineHeight: 1.5,
                fontFamily: "'Cormorant Garamond',serif" }}>
                Type a question and Oscar will help!
              </p>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex',
                justifyContent: m.from === 'user' ? 'flex-end' : 'flex-start',
                marginBottom: 8 }}>
                <div style={{
                  background: m.from === 'user'
                    ? 'linear-gradient(145deg,#D4AA50,#C49830)'
                    : 'rgba(255,253,246,0.90)',
                  color: m.from === 'user' ? '#FFF5D0' : '#1A1A1A',
                  padding: '9px 13px', borderRadius: 11, maxWidth: '85%',
                  fontSize: 14, lineHeight: 1.55,
                  border: m.from === 'oscar' ? '1px solid rgba(200,170,60,0.35)' : 'none',
                  fontFamily: "'Cormorant Garamond',serif",
                }}>{m.text}</div>
              </div>
            ))}
            {sending && (
              <div style={{ display: 'flex' }}>
                <div style={{ background: 'rgba(255,253,246,0.90)',
                  border: '1px solid rgba(200,170,60,0.30)',
                  padding: '9px 13px', borderRadius: 11,
                  fontSize: 14, color: 'rgba(60,50,30,0.65)',
                  fontStyle: 'italic', fontFamily: "'Cormorant Garamond',serif" }}>
                  Typing...
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <div style={{ display: 'flex', gap: 8 }}>
            <style>{`.oscar-input::placeholder { color: #1A1A1A !important; opacity: 1; }`}</style>
            <input
              className="oscar-input"
              style={{ flex: 1, border: '1px solid rgba(200,170,60,0.50)',
                borderRadius: 10, padding: '10px 12px', fontSize: 15, outline: 'none',
                fontFamily: "'Cormorant Garamond',serif",
                background: 'rgba(255,253,246,0.90)', color: '#1A1A1A' }}
              placeholder="Ask Oscar anything..."
              value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendMsg()}
              maxLength={500}
            />
            <button onClick={() => sendMsg()}
              disabled={sending || !input.trim()}
              style={{
                background: 'linear-gradient(145deg,#D4AA50 0%,#C49830 40%,#B48820 70%,#C49830 100%)',
                border: 'none', borderRadius: 10,
                width: 42, height: 42, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: sending || !input.trim() ? 'default' : 'pointer',
                opacity: sending || !input.trim() ? 0.55 : 1,
                boxShadow: '0 2px 8px rgba(180,130,20,0.28)',
                alignSelf: 'flex-end',
              }}>
              <Send size={16} color="#FFF5D0" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main ExploreEntrySection ─────────────────────────────────────────────────
const ExploreEntrySection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const urlToken = new URLSearchParams(location.search).get('token');
  const scrollRef          = useRef(null);
  const [canScrollLeft,  setCanScrollLeft]  = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeIndex, setActiveIndex]       = useState(0);
  const [hoveredId,   setHoveredId]         = useState(null);
  const [expandedPanel, setExpandedPanel]   = useState(null);
  const [siteConfig, setSiteConfig]         = useState(null);
  const [guestName,  setGuestName]          = useState('');
  const [displayName, setDisplayName]       = useState('');
  const [subtitles, setSubtitles]           = useState({
    travel:       'Getting to our celebration',
    things_to_do: 'Explore the area',
    calendar:     "See Carol's Schedule",
  });
  const [isMobile, setIsMobile]             = useState(() => window.innerWidth < 768);

  useEffect(() => {
    // Fetch template-driven config (replaces guest-site-config)
    const tokenParam = urlToken ? `?token=${urlToken}` : '';
    fetch(`${API}/api/public/guest-experience${tokenParam}`)
      .then(r => r.json())
      .then(d => {
        // Map template data to the siteConfig format the panels expect
        setSiteConfig(d);
        // Use guest name from template resolution if available
        if (d._guest_name) setGuestName(d._guest_name.split(' ')[0]);
        // _party_display_name uses the same rule as RSVP greeting / Compose
        // Invite — Concierge title reads from this. (Feb 2026.)
        if (d._party_display_name) setDisplayName(d._party_display_name);
        // Update subtitles from discover_cards if present
        if (d?.discover_cards && Array.isArray(d.discover_cards)) {
          const cards = d.discover_cards;
          const calCard = cards.find(c => c.id === 'calendar' || c.type === 'calendar');
          const hotelCard = cards.find(c => c.id === 'hotels' || c.type === 'hotels');
          const njCard = cards.find(c => c.id === 'nj-guide' || c.type === 'info');
          setSubtitles({
            travel:       hotelCard?.subtitle || 'Getting to our celebration',
            things_to_do: njCard?.subtitle    || 'Explore the area',
            calendar:     calCard?.subtitle   || "See Carol's Schedule",
          });
        } else if (d?.discover_cards && !Array.isArray(d.discover_cards)) {
          // Legacy format from guest_site_config
          setSubtitles({
            travel:       d.discover_cards.travel?.subtitle       || 'Getting to our celebration',
            things_to_do: d.discover_cards.things_to_do?.subtitle || 'Explore the area',
            calendar:     d.discover_cards.calendar?.subtitle     || "See Carol's Schedule",
          });
        }
        // Use concierge greeting from template if no guest name from token
        if (!urlToken && d?.concierge?.greeting_template) {
          // No personalization without token — greeting stays default
        }
      })
      .catch(() => {});
  }, [urlToken]);

  // Guest name is now provided by the template resolution endpoint above

  // Responsive: track mobile / desktop
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Lock body scroll when an overlay panel is open
  useEffect(() => {
    if (expandedPanel) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [expandedPanel]);

  // ESC key closes overlay panels
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closePanel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expandedPanel]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!SHOW_EXPLORE) return null;

  // Derive enabled state from template discover_cards (array format) or legacy config.
  // For tiles that didn't previously have an enabled toggle (Calendar, Concierge),
  // we default to TRUE when the admin hasn't explicitly disabled the card —
  // preserving the current always-visible default behavior.
  const discoverCards = siteConfig?.discover_cards;
  const hotelsEnabled = Array.isArray(discoverCards)
    ? discoverCards.some(c => (c.id === 'hotels' || c.type === 'hotels') && c.enabled)
    : siteConfig?.travel?.hotels?.enabled;
  const flightsEnabled = Array.isArray(discoverCards)
    ? discoverCards.some(c => (c.id === 'flights' || c.type === 'flights') && c.enabled)
    : siteConfig?.travel?.flights?.enabled;
  const thingsToDoEnabled = Array.isArray(discoverCards)
    ? discoverCards.some(c => (c.id === 'nj-guide' || c.type === 'info') && c.enabled)
    : siteConfig?.things_to_do?.enabled;
  const cardEnabledOrDefault = (cid) => {
    if (!Array.isArray(discoverCards)) return true;
    const found = discoverCards.find(c => c.id === cid);
    if (!found) return true;            // tile not in array → preserve historic default
    return found.enabled !== false;     // explicit false hides; missing/true shows
  };
  const calendarEnabled  = cardEnabledOrDefault('calendar');
  const conciergeEnabled = cardEnabledOrDefault('concierge');

  const baseItems = [];

  // Map current discover_cards (admin-editable) by id so the carousel
  // can prefer template-driven title/subtitle, falling back to the
  // hardcoded defaults below when the admin hasn't customised them.
  // (Item B / Phase 2 — Feb 2026: Hotels, Flights, Concierge, Calendar,
  // NJ Guide titles + descriptions are now fully admin-editable.)
  const cardsArr = Array.isArray(discoverCards) ? discoverCards : [];
  const cardById = (cid) => cardsArr.find(c => c.id === cid) || {};
  const calendarCard  = cardById('calendar');
  const hotelsCard    = cardById('hotels');
  const flightsCard   = cardById('flights');
  const conciergeCard = cardById('concierge');
  const njGuideCard   = cardById('nj-guide');

  if (calendarEnabled) {
    baseItems.push({
      id:'calendar', icon:Calendar,
      title:       (calendarCard.title    || '').trim() || 'Calendar',
      description: (calendarCard.subtitle || '').trim() || subtitles.calendar,
      action:'expand', panel:'calendar',
    });
  }

  const allItems = [
    ...baseItems,
    // POSITION SWAP (Apr 2026): Concierge now takes the slot Hotels previously
    // occupied (#2 after Calendar), and Hotels takes the slot Concierge
    // previously occupied (#4 after Flights). Click destinations and
    // panel routing are preserved — only carousel order changes.
    ...(conciergeEnabled ? [{
      id:'concierge', icon:ConciergeBell,
      title:       (conciergeCard.title    || '').trim() || 'Concierge',
      description: (conciergeCard.subtitle || '').trim() || 'Ask me any questions',
      action:'expand', panel:'concierge',
    }] : []),
    ...(flightsEnabled ? [{
      id:'flights', icon:Plane,
      title:       (flightsCard.title    || '').trim() || 'Flights',
      description: (flightsCard.subtitle || '').trim() || 'Getting here',
      action:'expand', panel:'flights',
    }] : []),
    ...(hotelsEnabled ? [{
      id:'hotels', icon:BedDouble,
      title:       (hotelsCard.title    || '').trim() || 'Hotels',
      description: (hotelsCard.subtitle || '').trim() || 'Where to stay',
      action:'expand', panel:'hotels',
    }] : []),
    ...(thingsToDoEnabled ? [{
      id:'nj-guide', icon:MapPin,
      title:       (njGuideCard.title    || '').trim() || 'NJ Guide',
      description: (njGuideCard.subtitle || '').trim() || subtitles.things_to_do,
      action:'expand', panel:'nj-guide',
    }] : []),
  ];

  const updateScrollState = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
      setActiveIndex(Math.min(Math.round(scrollLeft / 212), allItems.length - 1));
    }
  };

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ left: scrollRef.current.scrollLeft + (dir === 'left' ? -212 : 212), behavior:'smooth' });
      setTimeout(updateScrollState, 350);
    }
  };

  const handleTileClick = (item) => {
    if (item.action === 'expand') {
      if (expandedPanel === item.panel) {
        closePanel();
      } else {
        setExpandedPanel(item.panel);
      }
    } else {
      navigate(item.route);
    }
  };

  const closePanel = () => {
    setExpandedPanel(null);
  };

  return (
    <div id="explore" style={{ width:'100%', padding:'32px 0', position:'relative' }}>

      {/* Section header — no "Discover" label */}
      <div style={{ textAlign:'center', marginBottom:24, padding:'0 16px' }}>
        <h2 style={{ fontFamily:"'Cinzel',serif", fontSize:28, fontWeight:600,
          color:'#1E1A14', margin:0, letterSpacing:'0.5px' }}>
          Your Private Concierge
        </h2>
        <div style={{ display:'flex', alignItems:'center', gap:8, justifyContent:'center', marginTop:12 }}>
          <div style={{ width:28, height:1, background:'linear-gradient(90deg, transparent, rgba(160,120,20,0.80))' }} />
          <span style={{ color:'rgba(160,120,20,0.90)', fontSize:9 }}>✦</span>
          <div style={{ width:28, height:1, background:'linear-gradient(90deg, rgba(160,120,20,0.80), transparent)' }} />
        </div>
      </div>

      {/* Carousel */}
      <div style={{ position:'relative' }}>
        {canScrollLeft && (
          <button onClick={() => scroll('left')} data-testid="carousel-left-arrow"
            style={{ position:'absolute', left:6, top:'50%', transform:'translateY(-50%)',
              width:38, height:38, borderRadius:'50%', border:'none', cursor:'pointer', zIndex:10,
              background:'rgba(20,15,8,0.65)', backdropFilter:'blur(8px)', WebkitBackdropFilter:'blur(8px)',
              boxShadow:'0 0 0 1px rgba(200,170,60,0.35), 0 4px 12px rgba(0,0,0,0.25)',
              display:'flex', alignItems:'center', justifyContent:'center' }}>
            <ChevronLeft size={18} color="rgba(200,170,60,0.90)" />
          </button>
        )}
        {canScrollRight && (
          <button onClick={() => scroll('right')} data-testid="carousel-right-arrow"
            style={{ position:'absolute', right:6, top:'50%', transform:'translateY(-50%)',
              width:38, height:38, borderRadius:'50%', border:'none', cursor:'pointer', zIndex:10,
              background:'rgba(20,15,8,0.65)', backdropFilter:'blur(8px)', WebkitBackdropFilter:'blur(8px)',
              boxShadow:'0 0 0 1px rgba(200,170,60,0.35), 0 4px 12px rgba(0,0,0,0.25)',
              display:'flex', alignItems:'center', justifyContent:'center' }}>
            <ChevronRight size={18} color="rgba(200,170,60,0.90)" />
          </button>
        )}

        <div ref={scrollRef} onScroll={updateScrollState}
          style={{ display:'flex', gap:12, overflowX:'scroll', scrollSnapType:'x mandatory',
            WebkitOverflowScrolling:'touch', scrollPaddingInline:'20px',
            padding:'12px 20px 16px', scrollbarWidth:'none', msOverflowStyle:'none' }}>
          {allItems.map((item) => {
            const Icon = item.icon;
            const isHovered = hoveredId === item.id;
            const isActive = expandedPanel === item.panel;
            // Concierge tile (Apr 2026): full-tile Oscar image replaces the
            // icon + title + subtitle + dot divider. Gold corner crusting,
            // hover bloom, click behavior, and ConciergePanel routing all
            // remain unchanged.
            const isConcierge = item.id === 'concierge';
            return (
              <div key={item.id} data-testid={`explore-card-${item.id}`}
                onClick={() => handleTileClick(item)}
                onMouseEnter={() => setHoveredId(item.id)}
                onMouseLeave={() => setHoveredId(null)}
                aria-label={isConcierge ? 'Oscar — Concierge' : undefined}
                style={{
                  ...darkGlass,
                  flexShrink:0, width:200, minHeight:224, borderRadius:20,
                  scrollSnapAlign:'center', cursor:'pointer',
                  display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                  padding: isConcierge ? 0 : '32px 16px 28px', position:'relative', overflow:'hidden',
                  transform: (isHovered || isActive) ? 'translateY(-5px)' : 'translateY(0)',
                  transition:'transform 0.45s cubic-bezier(0.22,1,0.36,1), box-shadow 0.45s ease',
                  boxShadow: (isHovered || isActive)
                    ? ['inset 0 2px 0 rgba(255,255,255,0.18)', 'inset 0 -1px 0 rgba(212,168,83,0.40)',
                       '0 28px 56px rgba(0,0,0,0.35)', '0 8px 20px rgba(180,140,30,0.25)'].join(', ')
                    : darkGlass.boxShadow,
                  ...(isActive ? { border:'1.5px solid rgba(200,170,60,0.55)' } : {}),
                }}>
                {/* Concierge full-bleed Oscar image (sits BENEATH the gold accents/overlays) */}
                {isConcierge && (
                  <>
                    <img
                      src="/oscar-tile.webp"
                      alt=""
                      aria-hidden="true"
                      draggable={false}
                      decoding="async"
                      loading="lazy"
                      data-testid="explore-card-concierge-image"
                      style={{
                        position:'absolute', inset:0, width:'100%', height:'100%',
                        objectFit:'cover', objectPosition:'center center',
                        borderRadius:20, pointerEvents:'none', zIndex:0,
                        transform: (isHovered || isActive) ? 'scale(1.03)' : 'scale(1)',
                        transition:'transform 0.5s cubic-bezier(0.22,1,0.36,1)',
                      }}
                    />
                    {/* Subtle premium vignette — radial dim only at the very edges so
                        Oscar stays bright while the corners fall off elegantly into
                        the gold-accent frame. Tasteful, not muddy or dramatic. */}
                    <div aria-hidden="true" style={{
                      position:'absolute', inset:0, borderRadius:20, pointerEvents:'none',
                      background:
                        'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.18) 88%, rgba(0,0,0,0.32) 100%)',
                      zIndex:1,
                    }} />
                  </>
                )}

                {/* Gold crusting — top accent bar */}
                <div style={{ position:'absolute', top:0, left:0, right:0, height:3, pointerEvents:'none',
                  background:'linear-gradient(90deg, transparent 0%, rgba(180,140,30,0.55) 15%, rgba(220,185,60,0.90) 50%, rgba(180,140,30,0.55) 85%, transparent 100%)',
                  opacity:(isHovered||isActive) ? 1 : 0.65, transition:'opacity 0.35s ease', zIndex:2 }} />
                {/* Inner sheen just below gold bar */}
                <div style={{ position:'absolute', top:3, left:0, right:0, height:1, pointerEvents:'none',
                  background:'linear-gradient(90deg, transparent, rgba(255,255,255,0.80) 30%, rgba(255,255,255,0.80) 70%, transparent)', zIndex:2 }} />
                {/* Corner accents — top-left */}
                <div style={{ position:'absolute', top:10, left:10, width:12, height:12, pointerEvents:'none',
                  borderTop:'1.5px solid rgba(200,170,60,0.55)', borderLeft:'1.5px solid rgba(200,170,60,0.55)',
                  opacity:(isHovered||isActive) ? 1 : 0.55, transition:'opacity 0.35s ease', zIndex:2 }} />
                {/* Corner accents — top-right */}
                <div style={{ position:'absolute', top:10, right:10, width:12, height:12, pointerEvents:'none',
                  borderTop:'1.5px solid rgba(200,170,60,0.55)', borderRight:'1.5px solid rgba(200,170,60,0.55)',
                  opacity:(isHovered||isActive) ? 1 : 0.55, transition:'opacity 0.35s ease', zIndex:2 }} />
                {/* Corner accents — bottom-left */}
                <div style={{ position:'absolute', bottom:10, left:10, width:12, height:12, pointerEvents:'none',
                  borderBottom:'1.5px solid rgba(200,170,60,0.40)', borderLeft:'1.5px solid rgba(200,170,60,0.40)',
                  opacity:(isHovered||isActive) ? 1 : 0.40, transition:'opacity 0.35s ease', zIndex:2 }} />
                {/* Corner accents — bottom-right */}
                <div style={{ position:'absolute', bottom:10, right:10, width:12, height:12, pointerEvents:'none',
                  borderBottom:'1.5px solid rgba(200,170,60,0.40)', borderRight:'1.5px solid rgba(200,170,60,0.40)',
                  opacity:(isHovered||isActive) ? 1 : 0.40, transition:'opacity 0.35s ease', zIndex:2 }} />
                {/* Hover bloom */}
                <div style={{ position:'absolute', top:-20, left:'50%', transform:'translateX(-50%)',
                  width:80, height:80, borderRadius:'50%', pointerEvents:'none',
                  background:'radial-gradient(circle, rgba(200,170,60,0.18) 0%, transparent 70%)',
                  filter:'blur(14px)', opacity:(isHovered||isActive) ? 1 : 0, transition:'opacity 0.4s ease', zIndex:2 }} />
                {/* Bottom gold accent line */}
                <div style={{ position:'absolute', bottom:0, left:'20%', right:'20%', height:1, pointerEvents:'none',
                  background:'linear-gradient(90deg, transparent, rgba(200,170,60,0.55), transparent)',
                  opacity:(isHovered||isActive) ? 1 : 0.4, transition:'opacity 0.4s ease', zIndex:2 }} />

                {/* Icon + title + subtitle + dot divider — rendered for every tile EXCEPT Concierge */}
                {!isConcierge && (
                  <>
                    <div style={{ marginBottom:14, transition:'color 0.3s ease, transform 0.4s ease',
                      color:(isHovered||isActive) ? '#E2BF50' : 'rgba(200,170,60,0.75)',
                      transform:(isHovered||isActive) ? 'scale(1.10)' : 'scale(1)' }}>
                      <Icon size={34} />
                    </div>
                    <p style={{ fontFamily:"'Cinzel',serif", fontSize:17, fontWeight:600,
                      textTransform:'uppercase', letterSpacing:'1.5px',
                      color:'rgba(255,255,255,0.95)', margin:'0 0 6px', textAlign:'center' }}>
                      {item.title}
                    </p>
                    <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:17, fontStyle:'italic',
                      color:'rgba(255,255,255,0.85)', margin:0, textAlign:'center' }}>
                      {item.description}
                    </p>
                    <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:14,
                      opacity:(isHovered||isActive) ? 0.85 : 0.45, transition:'opacity 0.4s ease' }}>
                      <div style={{ width:18, height:1, background:'rgba(200,170,60,0.70)' }} />
                      <div style={{ width:4, height:4, borderRadius:'50%', background:'rgba(200,170,60,0.85)' }} />
                      <div style={{ width:18, height:1, background:'rgba(200,170,60,0.70)' }} />
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Dot indicators */}
      <div style={{ display:'flex', justifyContent:'center', gap:6, marginTop:4 }}>
        {allItems.map((_, i) => (
          <div key={i} style={{ width:i === activeIndex ? 18 : 6, height:6, borderRadius:3,
            background: i === activeIndex ? 'rgba(200,170,60,0.85)' : 'rgba(200,170,60,0.28)',
            transition:'all 0.3s ease' }} />
        ))}
      </div>

      {/* ── Overlay panels: Calendar, Hotels, Flights, NJ Guide, Concierge ─── */}
      {expandedPanel && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 999,
            background: 'rgba(0,0,0,0.65)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: isMobile ? 'flex-end' : 'center',
            justifyContent: 'center',
            padding: isMobile ? 0 : 24,
            boxSizing: 'border-box',
          }}
          onClick={closePanel}
        >
          {/* Panel container — clips at rounded corners */}
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: isMobile ? '100%' : 'min(540px, calc(100vw - 48px))',
              maxHeight: isMobile ? '85vh' : '80vh',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: isMobile ? '20px 20px 0 0' : '20px',
              overflow: 'hidden',
              background: 'rgba(255,252,240,0.92)',
            }}
          >
            <div style={{
              overflowY: 'auto', flex: 1,
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none', msOverflowStyle: 'none',
            }}>
              {expandedPanel === 'hotels'     && <HotelsPanel config={siteConfig} onClose={closePanel} />}
              {expandedPanel === 'flights'    && <FlightsPanel config={siteConfig} onClose={closePanel} />}
              {expandedPanel === 'nj-guide'   && <NJGuidePanel config={siteConfig} onClose={closePanel} />}
              {expandedPanel === 'calendar'   && <GuestCalendarPanel onClose={closePanel} />}
              {expandedPanel === 'concierge'  && <ConciergePanel guestName={guestName} displayName={displayName} conciergeConfig={siteConfig?.concierge} onClose={closePanel} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExploreEntrySection;
